import { useEffect } from 'react'
import { Switch, Route, Redirect } from 'wouter'
import { AuthProvider, useAuth } from './AuthContext'
import { ThemeProvider } from './theme'
import StagingBanner from './components/StagingBanner'
import MiseAJourBanner from './components/MiseAJourBanner'
import useMarchesPublics from './hooks/useMarchesPublics'
import usePwaInstall from './hooks/usePwaInstall'

// Pages externes
import PageConnexion from './PageConnexion'
import PageDesign from './pages/PageDesign'

// Modules plein ecran (une route chacun, autonomes — ADR-065/S48)
import MarcheModule from './pages/MarcheModule'
import MarcheModuleProducteur from './pages/MarcheModuleProducteur'
import PaniersModule from './pages/PaniersModule'
import PaniersProducerModule from './pages/PaniersProducerModule'
import PageProducteurPublic from './pages/PageProducteurPublic'

// Pages du hub — chaque page integre SON layout PageHub (retour, barre) :
// les routes ne font que router.
import PageAccueilClient from './pages/PageAccueilClient'
import PageExplorer from './pages/PageExplorer'
import PageExplorerProducteur from './pages/PageExplorerProducteur'
import PageFavoris from './pages/PageFavoris'
import PageCompte from './pages/PageCompte'
import PageProfil from './pages/account/PageProfil'
import PageSecurite from './pages/account/PageSecurite'
import PageDossierExposant from './pages/account/dossier/PageDossierExposant'
import PageChangerPin from './pages/account/PageChangerPin'
import PageAide from './pages/account/PageAide'
import PageAideFaq from './pages/account/PageAideFaq'
import PageAideContact from './pages/account/PageAideContact'
import PageWallet from './pages/account/PageWallet'
import PageParametres from './pages/PageParametres'
import PageNotifications from './pages/PageNotifications'
import PageAccueilProducteur from './pages/PageAccueilProducteur'
import PageActiviteProducteur from './pages/PageActiviteProducteur'
import PageMarchesProducteur from './pages/PageMarchesProducteur'
import PageStandProducteur from './pages/PageStandProducteur'
import PageOffres from './pages/PageOffres'
import PageOrganisation from './pages/PageOrganisation'
import PageProfilProducteur from './pages/PageProfilProducteur'
import PageFacturation from './pages/PageFacturation'
import PagePaiements from './pages/PagePaiements'

// Blocs de l'app
import FondPage from './components/FondPage'
import PwaInstallBanners from './components/PwaInstallBanners'
import ImmersiveScreen from './components/ImmersiveScreen'
import RinaChatModal from './components/RinaChatModal'

// Contextes (ADR-066/067) : chacun UNE responsabilite, consommable en profondeur
import { ROUTES, CHEMINS_ONGLETS } from './routes'
import { MVP } from './mvp'
import { ModalesProvider } from './contexts/modales'
import { NavigationPortailProvider, useNavigationPortail, useActionsPortail } from './contexts/navigation'
import { CasquetteProvider, useCasquette } from './contexts/casquette'
import { PanierProvider } from './contexts/panier'

// ============================================
// APP = l'empilement des providers + LA table de routage : un Switch PLAT,
// toutes les adresses viennent de ROUTES/CHEMINS_ONGLETS (zero chaine en dur,
// zero nest). Sous /producteur la casquette bascule via l'URL (ADR-068,
// effet dans contexts/navigation.jsx).
// ============================================
export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <CasquetteProvider>
          <NavigationPortailProvider>
            <PanierProvider>
              <ModalesProvider>
                <StagingBanner />
                <MiseAJourBanner />
                <AppContent />
              </ModalesProvider>
            </PanierProvider>
          </NavigationPortailProvider>
        </CasquetteProvider>
      </AuthProvider>
    </ThemeProvider>
  )
}

const C = CHEMINS_ONGLETS

// MVP (S62) : une page masquee par drapeau garde sa route mais REDIRIGE vers
// l'accueil — un ancien lien ne tombe jamais sur un ecran vide.
const siOuvert = (ouvert, page) => (ouvert ? page : <Redirect to={ROUTES.accueil} />)

// GARDE DE L'ESPACE PRODUCTEUR (S62, remonte par Daniel : /producteur/profil
// s'ouvrait en navigation privee). Visiteur → connexion ; connecte mais pas
// producteur valide → accueil client (et la casquette revient a « client »,
// car entrer sous /producteur l'avait basculee) ; verification en cours → rien.
function ZoneProducteur({ children }) {
  const { isAuthenticated } = useAuth()
  const { isProducer, producteurVerifie, setHubMode } = useCasquette()
  const refuse = isAuthenticated && producteurVerifie && !isProducer
  useEffect(() => { if (refuse) setHubMode('client') }, [refuse])
  if (!isAuthenticated) return <Redirect to={ROUTES.connexion} />
  if (!producteurVerifie) return null
  if (!isProducer) return <Redirect to={ROUTES.accueil} />
  return children
}

// L'URL est la source de verite (wouter, ADR-065/068). Restent ici les trois
// overlays d'APPLICATION : bandeaux PWA, ecran immersif d'entree, page RINA.
function AppContent() {
  const { user, isAuthenticated } = useAuth()
  const { hubMode } = useCasquette()
  const etat = useNavigationPortail()
  const actions = useActionsPortail()
  const marches = useMarchesPublics()
  const pwa = usePwaInstall()

  return (
    <>
      <Switch>
        {/* Pages hors hub */}
        <Route path={ROUTES.design}>
          <FondPage><PageDesign onBack={() => actions.navigate('marche')} /></FondPage>
        </Route>
        <Route path={ROUTES.connexion}>
          <FondPage><PageConnexion onNavigate={actions.navigate} onSuccess={() => actions.navigate('marche')} /></FondPage>
        </Route>

        {/* Page publique d'un producteur (S62) — adresse partagee, fiche plein ecran */}
        <Route path={ROUTES.producteurPublic.motif}>
          {(params) => <PageProducteurPublic id={params.id} />}
        </Route>

        {/* Modules plein ecran */}
        <Route path={ROUTES.marche.motif}>
          {(params) => <MarcheModule marcheId={params.id} />}
        </Route>
        <Route path={ROUTES.paniers}>{siOuvert(MVP.PANIERS, <PaniersModule />)}</Route>
        <Route path={ROUTES.producteur.marche}><ZoneProducteur><MarcheModuleProducteur /></ZoneProducteur></Route>
        <Route path={ROUTES.producteur.paniers}><ZoneProducteur>{siOuvert(MVP.PANIERS, <PaniersProducerModule />)}</ZoneProducteur></Route>

        {/* Onglets — casquette CLIENT + onglets partages */}
        <Route path={C.client.home}><PageAccueilClient /></Route>
        <Route path={C.client.explore}>{siOuvert(MVP.EXPLORER_CLIENT, <PageExplorer />)}</Route>
        <Route path={C.client.favorites}>{siOuvert(MVP.FAVORIS, <PageFavoris />)}</Route>
        <Route path={ROUTES.compte.base}><PageCompte /></Route>
        <Route path={ROUTES.compte.profil}><PageProfil /></Route>
        <Route path={ROUTES.compte.securite}><PageSecurite /></Route>
        <Route path={ROUTES.compte.dossier}>{siOuvert(MVP.DOSSIER_EXPOSANT, <PageDossierExposant />)}</Route>
        <Route path={ROUTES.compte.pin}><PageChangerPin /></Route>
        <Route path={ROUTES.compte.aide}><PageAide /></Route>
        <Route path={ROUTES.compte.aideFaq}><PageAideFaq /></Route>
        <Route path={ROUTES.compte.aideContact}><PageAideContact /></Route>
        <Route path={C.client.wallet}>{siOuvert(MVP.WALLET, <PageWallet />)}</Route>
        <Route path={C.client.parametres}>{siOuvert(MVP.PARAMETRES, <PageParametres />)}</Route>
        <Route path={C.client.notifications}>{siOuvert(MVP.NOTIFICATIONS, <PageNotifications />)}</Route>

        {/* Onglets — espace PRODUCTEUR (/producteur/*) */}
        <Route path={C.producer.home}><ZoneProducteur><PageAccueilProducteur /></ZoneProducteur></Route>
        <Route path={C.producer.explore}><ZoneProducteur>{MVP.EXPLORER_PRODUCTEUR ? <PageExplorerProducteur /> : <Redirect to={ROUTES.producteur.accueil} />}</ZoneProducteur></Route>
        <Route path={C.producer.activite}><ZoneProducteur>{siOuvert(MVP.PRODUCTEUR_ACTIVITE, <PageActiviteProducteur />)}</ZoneProducteur></Route>
        <Route path={C.producer.offers}><ZoneProducteur><PageOffres /></ZoneProducteur></Route>
        <Route path={C.producer.stand}><ZoneProducteur><PageStandProducteur /></ZoneProducteur></Route>
        <Route path={C.producer.marches}><ZoneProducteur><PageMarchesProducteur /></ZoneProducteur></Route>
        <Route path={C.producer.profilprod}><ZoneProducteur><PageProfilProducteur /></ZoneProducteur></Route>
        <Route path={C.producer.organisation}><ZoneProducteur>{siOuvert(MVP.PRODUCTEUR_ORGANISATION, <PageOrganisation />)}</ZoneProducteur></Route>
        <Route path={C.producer.facturation}><ZoneProducteur>{siOuvert(MVP.PRODUCTEUR_FACTURATION, <PageFacturation />)}</ZoneProducteur></Route>
        <Route path={C.producer.paiement}><ZoneProducteur>{siOuvert(MVP.PRODUCTEUR_PAIEMENTS, <PagePaiements />)}</ZoneProducteur></Route>

        {/* Chemin inconnu → accueil */}
        <Route><Redirect to={ROUTES.accueil} /></Route>
      </Switch>

      {/* Bandeaux installation PWA (Android/Desktop + iOS) */}
      <PwaInstallBanners pwa={pwa} page={etat.page} showImmersive={etat.showImmersive} />

      {/* Ecran immersif (premiere visite) */}
      {etat.showImmersive && (
        <ImmersiveScreen
          marches={marches}
          onExplore={(marche) => { actions.setShowImmersive(false); if (marche?.id) actions.openMarche(marche.id); else actions.setHubTab('home') }}
          onClose={() => { actions.setHubTab('home'); actions.setShowImmersive(false) }}
          onNavigate={actions.navigate}
          isAuthenticated={isAuthenticated}
          user={user}
        />
      )}

      {/* Page RINA (ouverte par les cartes contextuelles) */}
      {etat.showRinaChat && (
        <RinaChatModal
          onClose={() => actions.setShowRinaChat(false)}
          onNavigate={actions.navigate}
          isAuthenticated={isAuthenticated}
          user={user}
          hubMode={hubMode}
          lieu={etat.openMarcheId || etat.openMarcheProducer ? 'marche' : etat.openPaniers || etat.openPaniersProducer ? 'paniers' : etat.hubTab}
          onCta={(cta) => {
            if (cta?.vue) actions.ouvrirCompte(cta.vue)
            else if (cta?.tab) actions.setHubTab(cta.tab)
            else if (cta?.module === 'marche') { hubMode === 'producer' ? actions.ouvrirMarcheProducteur() : actions.setHubTab('explore') }
            else if (cta?.module === 'paniers') { hubMode === 'producer' ? actions.ouvrirPaniersProducteur() : actions.ouvrirPaniers() }
            window.scrollTo(0, 0)
          }}
          onExplore={(marche) => { actions.setShowRinaChat(false); if (marche?.id) actions.openMarche(marche.id); else actions.setHubTab('explore') }}
        />
      )}
    </>
  )
}
